import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import {
  FileText,
  Pin,
  Plus,
  Search,
  LayoutGrid,
  List,
  Layers,
  X,
  Lock,
  KeyRound,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Calendar,
  CheckSquare,
  Square,
  Check,
} from 'lucide-react';
import { Note } from '../../types';
import { NoteCard } from './NoteCard';
import { NoteEditorModal } from './NoteEditorModal';
import { NotePinModal } from './NotePinModal';
import { NoteReminderModal } from './NoteReminderModal';

export const NotesView: React.FC = () => {
  const {
    notes,
    addNote,
    updateNote,
    deleteNote,
    restoreNote,
    permanentlyDeleteNote,
    emptyTrash,
    togglePinNote,
    duplicateNote,
    language,
    t,
  } = useApp();

  const isBn = language === 'bn';

  const [activeTab, setActiveTab] = useState<'active' | 'trash'>('active');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'updated' | 'created' | 'title'>('updated');
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);

  // Multi-select bulk state
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Master PIN state
  const [masterPin, setMasterPin] = useState<string | null>(() => {
    return localStorage.getItem('mylife_notes_master_pin') || null;
  });

  // Modal State
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  // PIN Lock Modal State
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pinModalMode, setPinModalMode] = useState<'set' | 'unlock' | 'remove' | 'change'>('set');
  const [targetPinNote, setTargetPinNote] = useState<Note | null>(null);

  // Reminder Modal State
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [targetReminderNote, setTargetReminderNote] = useState<Note | null>(null);

  // Save Master PIN to storage
  const saveMasterPin = (newPin: string) => {
    localStorage.setItem('mylife_notes_master_pin', newPin);
    setMasterPin(newPin);
  };

  // Active vs Trashed Notes lists
  const activeNotesList = useMemo(() => notes.filter((n) => !n.isTrashed), [notes]);
  const trashedNotesList = useMemo(() => notes.filter((n) => n.isTrashed), [notes]);

  // Filtered and Sorted Active Notes
  const filteredActiveNotes = useMemo(() => {
    return activeNotesList
      .filter((n) => {
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchTitle = n.title.toLowerCase().includes(q);
          const matchContent = !n.isLocked && n.content?.toLowerCase().includes(q);
          return matchTitle || matchContent;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'title') {
          return a.title.localeCompare(b.title);
        }
        if (sortBy === 'created') {
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        }
        return (
          new Date(b.updatedAt || b.createdAt || 0).getTime() -
          new Date(a.updatedAt || a.createdAt || 0).getTime()
        );
      });
  }, [activeNotesList, searchQuery, sortBy]);

  // Filtered Trashed Notes
  const filteredTrashedNotes = useMemo(() => {
    return trashedNotesList.filter((n) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = n.title.toLowerCase().includes(q);
        const matchContent = !n.isLocked && n.content?.toLowerCase().includes(q);
        return matchTitle || matchContent;
      }
      return true;
    });
  }, [trashedNotesList, searchQuery]);

  // Pinned vs Others (in active list)
  const pinnedNotes = useMemo(
    () => filteredActiveNotes.filter((n) => n.pinned),
    [filteredActiveNotes]
  );
  const otherNotes = useMemo(
    () => filteredActiveNotes.filter((n) => !n.pinned),
    [filteredActiveNotes]
  );

  const totalActiveCount = activeNotesList.length;
  const lockedNotesCount = activeNotesList.filter((n) => n.isLocked).length;
  const trashedCount = trashedNotesList.length;

  // Clear selections when switching tabs
  const handleTabSwitch = (tab: 'active' | 'trash') => {
    setActiveTab(tab);
    setSelectedNoteIds([]);
  };

  // Selection handlers
  const toggleSelectNote = (id: string) => {
    setSelectedNoteIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const currentList = activeTab === 'active' ? filteredActiveNotes : filteredTrashedNotes;
    if (selectedNoteIds.length === currentList.length) {
      setSelectedNoteIds([]);
    } else {
      setSelectedNoteIds(currentList.map((n) => n.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedNoteIds.length === 0) return;
    if (activeTab === 'active') {
      selectedNoteIds.forEach((id) => deleteNote(id));
    } else {
      selectedNoteIds.forEach((id) => permanentlyDeleteNote(id));
    }
    setSelectedNoteIds([]);
  };

  const handleBulkRestore = () => {
    if (selectedNoteIds.length === 0) return;
    selectedNoteIds.forEach((id) => restoreNote(id));
    setSelectedNoteIds([]);
  };

  const handleBulkPin = () => {
    if (selectedNoteIds.length === 0) return;
    selectedNoteIds.forEach((id) => togglePinNote(id));
    setSelectedNoteIds([]);
  };

  const handleOpenNewNote = () => {
    setEditingNote(null);
    setIsEditorOpen(true);
  };

  const handleEditNote = (note: Note) => {
    if (note.isLocked) {
      handleUnlockNote(note);
      return;
    }
    setEditingNote(note);
    setIsEditorOpen(true);
  };

  // Lock note: if master PIN exists, lock immediately
  const handleLockNote = (note: Note) => {
    if (masterPin) {
      updateNote(note.id, { isLocked: true });
    } else {
      setTargetPinNote(note);
      setPinModalMode('set');
      setIsPinModalOpen(true);
    }
  };

  const handleUnlockNote = (note: Note) => {
    setTargetPinNote(note);
    setPinModalMode('unlock');
    setIsPinModalOpen(true);
  };

  const handleRemoveLock = (note: Note) => {
    setTargetPinNote(note);
    setPinModalMode('remove');
    setIsPinModalOpen(true);
  };

  const handleManageMasterPin = () => {
    setTargetPinNote(null);
    setPinModalMode(masterPin ? 'change' : 'set');
    setIsPinModalOpen(true);
  };

  const handleOpenReminderModal = (note: Note) => {
    setTargetReminderNote(note);
    setIsReminderModalOpen(true);
  };

  const handleSaveReminder = (noteId: string, reminderDate?: string) => {
    updateNote(noteId, { reminder: reminderDate });
  };

  const handlePinSuccess = (enteredPin?: string) => {
    if (pinModalMode === 'set' || pinModalMode === 'change') {
      if (enteredPin) {
        saveMasterPin(enteredPin);
        if (targetPinNote) {
          updateNote(targetPinNote.id, { isLocked: true });
        }
      }
    } else if (pinModalMode === 'unlock') {
      if (targetPinNote) {
        setEditingNote(targetPinNote);
        setIsEditorOpen(true);
      }
    } else if (pinModalMode === 'remove') {
      if (targetPinNote) {
        updateNote(targetPinNote.id, { isLocked: false });
      }
    }
  };

  const handleSaveNote = (noteData: Partial<Note>) => {
    if (editingNote && editingNote.id) {
      updateNote(editingNote.id, noteData);
    } else {
      addNote({
        title: noteData.title || 'Untitled Note',
        content: noteData.content || '',
        folder: 'General',
        tags: [],
        pinned: Boolean(noteData.pinned),
        isLocked: Boolean(noteData.isLocked),
        reminder: noteData.reminder,
      });
    }
  };

  const currentDisplayList = activeTab === 'active' ? filteredActiveNotes : filteredTrashedNotes;
  const isAllSelected =
    currentDisplayList.length > 0 && selectedNoteIds.length === currentDisplayList.length;

  return (
    <div className="space-y-6 animate-fade-in pb-20">
      {/* Header with Title, Stats, Tabs & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
              {activeTab === 'trash' ? <Trash2 className="w-5 h-5 text-rose-500" /> : <FileText className="w-5 h-5" />}
            </div>
            <div>
              <h1 className={`text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2 ${isBn ? 'font-bangla' : ''}`}>
                <span>{activeTab === 'trash' ? (isBn ? 'ট্র্যাশ ক্যান' : 'Trash Can') : (isBn ? 'নোটস' : 'Notes')}</span>
                {activeTab === 'active' && lockedNotesCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1">
                    <Lock className="w-3 h-3" /> {lockedNotesCount} {isBn ? 'টি লক' : 'locked'}
                  </span>
                )}
              </h1>
            </div>
          </div>
        </div>

        {/* View Switcher, Tabs & Actions - Clean Responsive Organization */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 w-full md:w-auto">
          {/* Mobile Row 1: Tab Switcher + Primary Create Action */}
          <div className="flex items-center justify-between gap-2 w-full sm:w-auto">
            {/* Active vs Trash Tab Toggle */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200/80 dark:border-slate-700">
              <button
                onClick={() => handleTabSwitch('active')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeTab === 'active'
                    ? 'bg-white dark:bg-[#111827] text-purple-600 dark:text-purple-400 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>{isBn ? 'নোটস' : 'Notes'} ({totalActiveCount})</span>
              </button>
              <button
                onClick={() => handleTabSwitch('trash')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeTab === 'trash'
                    ? 'bg-white dark:bg-[#111827] text-rose-600 dark:text-rose-400 shadow-xs'
                    : 'text-slate-500 hover:text-rose-500 dark:hover:text-rose-400'
                }`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isBn ? 'ট্র্যাশ' : 'Trash'} ({trashedCount})</span>
              </button>
            </div>

            {/* Create Note Button (or Empty Trash button if in trash) */}
            {activeTab === 'trash' && trashedCount > 0 ? (
              <button
                onClick={() => setShowEmptyTrashConfirm(true)}
                className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold shadow-md shadow-rose-500/25 flex items-center gap-1.5 transition-all transform active:scale-95 flex-shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isBn ? 'ট্র্যাশ খালি' : 'Empty'}</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  if (activeTab === 'trash') setActiveTab('active');
                  handleOpenNewNote();
                }}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-purple-500/25 flex items-center gap-1.5 transition-all transform active:scale-95 flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span>{isBn ? 'নতুন নোট' : 'Create Note'}</span>
              </button>
            )}
          </div>

          {/* Mobile Row 2 / Desktop End: Secondary Tools (Select, PIN, View Toggle) */}
          <div className="flex items-center justify-between sm:justify-end gap-1.5 w-full sm:w-auto">
            <div className="flex items-center gap-1.5">
              {/* Select Mode Toggle */}
              <button
                onClick={() => {
                  setIsSelectionMode(!isSelectionMode);
                  if (isSelectionMode) setSelectedNoteIds([]);
                }}
                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5 ${
                  isSelectionMode
                    ? 'bg-purple-500 text-white border-purple-600 shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-purple-500'
                }`}
              >
                <CheckSquare className="w-3.5 h-3.5" />
                <span>{isSelectionMode ? (isBn ? 'সম্পন্ন' : 'Done') : (isBn ? 'সিলেক্ট' : 'Select')}</span>
              </button>

              {/* Master PIN Button */}
              <button
                onClick={handleManageMasterPin}
                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5 ${
                  masterPin
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-purple-500'
                    : 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800'
                }`}
                title={masterPin ? (isBn ? 'মাস্টার পিন পরিবর্তন করুন' : 'Change Notes PIN') : (isBn ? 'মাস্টার পিন সেট করুন' : 'Set Master Notes PIN')}
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>{masterPin ? (isBn ? 'পিন পরিবর্তন' : 'Change PIN') : (isBn ? 'পিন সেট' : 'Set PIN')}</span>
              </button>
            </div>

            {/* Grid vs List View Toggle */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200/80 dark:border-slate-700 flex-shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-xl transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-[#111827] text-purple-600 dark:text-purple-400 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="Grid View"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-xl transition-all ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-[#111827] text-purple-600 dark:text-purple-400 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating / Sticky Bulk Action Bar when items are selected */}
      {selectedNoteIds.length > 0 && (
        <div className="p-3.5 bg-purple-600 dark:bg-purple-900 text-white rounded-2xl shadow-xl flex items-center justify-between gap-3 animate-scale-in flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={handleSelectAll}
              className="p-1 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center gap-1.5 text-xs font-bold px-2 py-1"
            >
              {isAllSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
            </button>

            <span className="text-xs font-bold text-white/90">
              {selectedNoteIds.length} note{selectedNoteIds.length > 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'active' ? (
              <>
                <button
                  onClick={handleBulkPin}
                  className="px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                >
                  <Pin className="w-3.5 h-3.5" />
                  <span>Pin / Unpin</span>
                </button>

                <button
                  onClick={handleBulkDelete}
                  className="px-3.5 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs font-bold shadow-sm flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Move to Trash ({selectedNoteIds.length})</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleBulkRestore}
                  className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold shadow-sm flex items-center gap-1.5 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Restore ({selectedNoteIds.length})</span>
                </button>

                <button
                  onClick={handleBulkDelete}
                  className="px-3.5 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs font-bold shadow-sm flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete Forever ({selectedNoteIds.length})</span>
                </button>
              </>
            )}

            <button
              onClick={() => setSelectedNoteIds([])}
              className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded-xl"
              title="Cancel Selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Sort Bar - Compact Mobile Alignment */}
      <div className="flex items-center justify-between gap-2.5 p-3 sm:p-4 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-xs">
        {/* Search Box */}
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder={activeTab === 'trash' ? 'Search trashed notes...' : t.searchNotes}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Sort Dropdown */}
        <div className="relative flex-shrink-0">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="w-auto px-2.5 sm:px-3.5 py-2 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
          >
            <option value="updated">{isBn ? 'আপডেট অনুযায়ী' : 'Last Updated'}</option>
            <option value="created">{isBn ? 'তৈরির তারিখ' : 'Date Created'}</option>
            <option value="title">{isBn ? 'নাম অনুযায়ী (A-Z)' : 'Title (A-Z)'}</option>
          </select>
        </div>
      </div>

      {/* TRASH TAB VIEW */}
      {activeTab === 'trash' ? (
        <div className="space-y-4">
          {trashedNotesList.length > 0 && (
            <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 text-xs flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span>Notes in the Trash can can be restored or permanently removed anytime.</span>
              </div>
              <button
                onClick={() => setShowEmptyTrashConfirm(true)}
                className="font-bold underline hover:text-rose-500 text-xs whitespace-nowrap flex-shrink-0"
              >
                Empty Trash ({trashedCount})
              </button>
            </div>
          )}

          {filteredTrashedNotes.length === 0 ? (
            <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto">
                <Trash2 className="w-7 h-7" />
              </div>
              <h3 className="text-base font-black text-slate-900 dark:text-white">
                {searchQuery ? 'No matching trashed notes' : 'Trash is Empty'}
              </h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {searchQuery
                  ? 'No deleted notes matched your search.'
                  : 'Deleted notes will be safely preserved here until you choose to empty the trash.'}
              </p>
              <button
                onClick={() => handleTabSwitch('active')}
                className="mt-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition-colors"
              >
                ← Back to Active Notes
              </button>
            </div>
          ) : (
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5'
                  : 'space-y-3'
              }
            >
              {filteredTrashedNotes.map((note) => {
                const isSelected = selectedNoteIds.includes(note.id);
                return (
                  <div
                    key={note.id}
                    onClick={() => {
                      if (isSelectionMode) {
                        toggleSelectNote(note.id);
                      }
                    }}
                    className={`bg-white dark:bg-[#111827] border rounded-3xl p-5 shadow-xs flex flex-col justify-between min-h-[170px] transition-all relative ${
                      isSelected
                        ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-500/[0.03]'
                        : 'border-rose-200/60 dark:border-rose-950/60 opacity-90 hover:opacity-100'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          {(isSelectionMode || selectedNoteIds.length > 0) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelectNote(note.id);
                              }}
                              className={`p-0.5 rounded-lg transition-colors ${
                                isSelected
                                  ? 'text-purple-600 dark:text-purple-400'
                                  : 'text-slate-300 dark:text-slate-600'
                              }`}
                            >
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4 fill-purple-500/10" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                          )}

                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                            In Trash
                          </span>
                        </div>

                        <span className="text-[11px] text-slate-400 font-mono">
                          {note.deletedAt ? new Date(note.deletedAt).toLocaleDateString() : 'Deleted'}
                        </span>
                      </div>

                      <h3 className="text-base font-bold leading-snug break-words mb-2 line-through text-slate-500 dark:text-slate-400">
                        {note.title}
                      </h3>

                      <p className="text-xs text-slate-400 dark:text-slate-500 line-clamp-3 leading-relaxed">
                        {note.isLocked
                          ? '•••••••••••• (Locked Note)'
                          : note.content || 'Empty note'}
                      </p>
                    </div>

                    {/* Trashed Card Actions */}
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 mt-4 flex items-center justify-between gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreNote(note.id);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-950/40 hover:bg-purple-100 text-purple-600 dark:text-purple-400 font-bold text-xs flex items-center gap-1.5 transition-colors"
                        title="Restore Note"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Restore</span>
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          permanentlyDeleteNote(note.id);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 text-rose-600 dark:text-rose-400 font-bold text-xs flex items-center gap-1.5 transition-colors"
                        title="Delete Permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Delete Forever</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ACTIVE NOTES TAB VIEW */
        filteredActiveNotes.length === 0 ? (
          <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-purple-500/10 text-purple-500 flex items-center justify-center mx-auto">
              <FileText className="w-7 h-7" />
            </div>
            <h3 className="text-base font-black text-slate-900 dark:text-white">
              {searchQuery ? 'No matching notes found' : 'No notes created yet'}
            </h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              {searchQuery
                ? 'Try adjusting your search query.'
                : 'Start capturing your thoughts, goals, and daily reminders in simple clean boxes!'}
            </p>
            <div className="pt-2 flex items-center justify-center gap-2">
              <button
                onClick={handleOpenNewNote}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl text-xs font-bold shadow-xs transition-colors"
              >
                + Create First Note
              </button>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* PINNED NOTES SECTION */}
            {pinnedNotes.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <Pin className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Pinned Notes ({pinnedNotes.length})
                  </h2>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                </div>

                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5'
                      : 'space-y-3'
                  }
                >
                  {pinnedNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      viewMode={viewMode}
                      isSelected={selectedNoteIds.includes(note.id)}
                      isSelectionMode={isSelectionMode || selectedNoteIds.length > 0}
                      onToggleSelect={toggleSelectNote}
                      onEdit={handleEditNote}
                      onDelete={deleteNote}
                      onTogglePin={togglePinNote}
                      onDuplicate={duplicateNote}
                      onLock={handleLockNote}
                      onUnlock={handleUnlockNote}
                      onRemoveLock={handleRemoveLock}
                      onAddReminder={handleOpenReminderModal}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* OTHER / ALL NOTES SECTION */}
            {otherNotes.length > 0 && (
              <div className="space-y-3">
                {pinnedNotes.length > 0 && (
                  <div className="flex items-center gap-2 px-1">
                    <Layers className="w-4 h-4 text-slate-400" />
                    <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Other Notes ({otherNotes.length})
                    </h2>
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                  </div>
                )}

                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5'
                      : 'space-y-3'
                  }
                >
                  {otherNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      viewMode={viewMode}
                      isSelected={selectedNoteIds.includes(note.id)}
                      isSelectionMode={isSelectionMode || selectedNoteIds.length > 0}
                      onToggleSelect={toggleSelectNote}
                      onEdit={handleEditNote}
                      onDelete={deleteNote}
                      onTogglePin={togglePinNote}
                      onDuplicate={duplicateNote}
                      onLock={handleLockNote}
                      onUnlock={handleUnlockNote}
                      onRemoveLock={handleRemoveLock}
                      onAddReminder={handleOpenReminderModal}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      {/* Note Editor / Creator Modal */}
      <NoteEditorModal
        isOpen={isEditorOpen}
        note={editingNote}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingNote(null);
        }}
        onSave={handleSaveNote}
        onDelete={deleteNote}
        onDuplicate={duplicateNote}
      />

      {/* Note PIN Lock / Unlock Modal */}
      <NotePinModal
        isOpen={isPinModalOpen}
        mode={pinModalMode}
        note={targetPinNote}
        masterPin={masterPin}
        onClose={() => {
          setIsPinModalOpen(false);
          setTargetPinNote(null);
        }}
        onSuccess={handlePinSuccess}
      />

      {/* Note Calendar Reminder Modal */}
      <NoteReminderModal
        isOpen={isReminderModalOpen}
        note={targetReminderNote}
        onClose={() => {
          setIsReminderModalOpen(false);
          setTargetReminderNote(null);
        }}
        onSaveReminder={handleSaveReminder}
      />

      {/* Empty Trash Confirmation Modal */}
      {showEmptyTrashConfirm && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEmptyTrashConfirm(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-sm bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 text-center space-y-4 animate-scale-in">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Empty Trash Can?
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Are you sure you want to permanently delete all {trashedCount} notes in the trash? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => setShowEmptyTrashConfirm(false)}
                className="flex-1 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  emptyTrash();
                  setShowEmptyTrashConfirm(false);
                }}
                className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/25"
              >
                Yes, Empty All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
